'use client';

import { c as _c } from "react/compiler-runtime";
import { CustomError } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { Comment } from '@/api/getCommentList';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';
import { scrollToCommentTarget, useCommentPostSuccess, useCommentThreadHashScroll } from './_hooks/useCommentThreadEffects';
import { useCommentThreadNavigation } from './_hooks/useCommentThreadNavigation';
import { CommentField, type CommentFieldProps, type CommentFieldSubmitResult } from './CommentField';
import type { CommentListProps } from './CommentList';
import { CommentList } from './CommentList';
type CommentFieldOptions = Pick<CommentFieldProps, 'className' | 'sticky' | 'topDivider' | 'topSlot' | 'withPortal'>;

/**
 * 답글/수정/idle 세 상태는 항상 배타라 단일 union으로 묶는다.
 * 두 useState로 나눠두면 setter 한쪽을 비우는 명령형 동기화가 매번 필요해 실수 가능성이 생긴다.
 */
type ComposerMode = {
  kind: 'idle';
} | {
  kind: 'reply';
  rootCommentId: number;
  parentCommentId: number;
  replyTo: string;
} | {
  kind: 'edit';
  commentId: number;
  contents: string;
};
const IDLE_COMPOSER: ComposerMode = {
  kind: 'idle'
};
export interface CommentThreadProps extends Pick<CommentListProps, 'commentType' | 'contentId' | 'initialData' | 'initialPageData' | 'rootCommentId' | 'className'> {
  /** 글 제목 */
  title?: string;
  /** 정책 이름 */
  policyName?: string;
  /** 첫 렌더 시 입력창에 포커스를 줄지 여부다. */
  initOnFocus?: boolean;
  /** 리스트만 쓰는 화면을 위해 입력창 렌더 여부를 분리한다. */
  hasCommentField?: boolean;
  /** 화면별 sticky/slot 차이를 주입하기 위한 CommentField 옵션 묶음이다. */
  commentFieldProps?: CommentFieldOptions;
}
export const CommentThread = t0 => {
  const $ = _c(67);
  const {
    initOnFocus,
    commentType,
    contentId,
    title,
    policyName,
    initialData,
    initialPageData,
    rootCommentId,
    className,
    hasCommentField: t1,
    commentFieldProps
  } = t0;
  const hasCommentField = t1 === undefined ? true : t1;
  const pathname = usePathname();
  const isLogin = useAuthStore(_temp);
  const router = useCustomRouter();
  const [commentData, setCommentData] = useState(initialData);
  const [composerMode, setComposerMode] = useState(IDLE_COMPOSER);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const [postNonce, setPostNonce] = useState(0);
  const {
    sortType,
    handleChangeSortType,
    resetSortType
  } = useCommentThreadNavigation();
  useCommentThreadHashScroll(pathname ?? "");
  let t2;
  if ($[0] !== commentType || $[1] !== contentId || $[2] !== policyName || $[3] !== resetSortType || $[4] !== sortType || $[5] !== title) {
    t2 = {
      commentType,
      contentId,
      title,
      policyName,
      sortType,
      resetSortType
    };
    $[0] = commentType;
    $[1] = contentId;
    $[2] = policyName;
    $[3] = resetSortType;
    $[4] = sortType;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleCommentPostSuccess = useCommentPostSuccess(t2);
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setFocusTrigger(_temp2);
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const openComposer = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setComposerMode(IDLE_COMPOSER);
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const clearComposerMode = t4;
  let t5;
  if ($[9] !== isLogin || $[10] !== router) {
    t5 = () => {
      if (isLogin) {
        return false;
      }
      const url = new URL(window.location.href);
      router.push(ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: `${url.pathname}${url.search}`
        }
      }));
      return true;
    };
    $[9] = isLogin;
    $[10] = router;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const requireLogin = t5;
  let t6;
  if ($[12] !== requireLogin) {
    t6 = (comment, nextRootCommentId, nextParentCommentId) => {
      if (requireLogin()) {
        return;
      }
      setComposerMode({
        kind: "reply",
        rootCommentId: nextRootCommentId,
        parentCommentId: nextParentCommentId,
        replyTo: comment.nickname
      });
      openComposer();
    };
    $[12] = requireLogin;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleClickReplyButton = t6;
  let t7;
  if ($[14] !== requireLogin) {
    t7 = comment_0 => {
      if (requireLogin()) {
        return;
      }
      setComposerMode({
        kind: "edit",
        commentId: comment_0.commentId,
        contents: comment_0.contents
      });
      openComposer();
    };
    $[14] = requireLogin;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleClickEditButton = t7;
  let t8;
  if ($[16] !== composerMode.commentId || $[17] !== composerMode.kind) {
    t8 = deletedCommentId => {
      if (composerMode.kind === "edit" && composerMode.commentId === deletedCommentId) {
        clearComposerMode();
      }
    };
    $[16] = composerMode.commentId;
    $[17] = composerMode.kind;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const clearEditModeByDeletedComment = t8;
  let t9;
  if ($[19] !== commentData || $[20] !== composerMode.commentId || $[21] !== composerMode.kind) {
    t9 = () => {
      if (composerMode.kind !== "edit" || !commentData) {
        return;
      }
      const findComment = comments => {
        for (const c of comments) {
          if (c.commentId === composerMode.commentId) {
            return c;
          }
          if (c.childCommentList) {
            const found = findComment(c.childCommentList);
            if (found) {
              return found;
            }
          }
        }
      };
      const target = findComment(commentData.commentList);
      const isHidden = !target || target.isBlocked || target.isDeleted || target.isReport || !target.isDisplay;
      if (isHidden) {
        clearComposerMode();
      }
    };
    $[19] = commentData;
    $[20] = composerMode.commentId;
    $[21] = composerMode.kind;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== commentData || $[24] !== composerMode) {
    t10 = [commentData, composerMode];
    $[23] = commentData;
    $[24] = composerMode;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] !== composerMode.kind || $[27] !== composerMode.replyTo) {
    t11 = composerMode.kind === "idle" ? null : <section className="flex items-center justify-between gap-12 rounded-small bg-Gray-50 px-12 py-8"><div className="min-w-0 flex-1"><strong className="block truncate font-600 text-Gray-900 typo-caption1">{composerMode.kind === "edit" ? "\uB313\uAE00 \uC218\uC815 \uC911" : "\uB2F5\uAE00 \uC791\uC131 \uC911"}</strong><p className="truncate text-Gray-700 typo-caption1">{composerMode.kind === "edit" ? "\uC218\uC815 \uD6C4 \uC804\uC1A1 \uBC84\uD2BC\uC744 \uB204\uB974\uBA74 \uB313\uAE00\uC774 \uBC14\uB85C \uBC18\uC601\uB3FC\uC694." : `@${composerMode.replyTo}님에게 답글을 남기고 있어요.`}</p></div><button className="shrink-0 font-500 text-Gray-700 typo-caption1" type="button" onClick={clearComposerMode}>취소</button></section>;
    $[26] = composerMode.kind;
    $[27] = composerMode.replyTo;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const contextualSlot = t11;
  let t12;
  if ($[29] !== commentFieldProps?.topSlot || $[30] !== contextualSlot) {
    t12 = !commentFieldProps?.topSlot && !contextualSlot ? undefined : <div className="flex flex-col gap-12">{commentFieldProps?.topSlot}{contextualSlot}</div>;
    $[29] = commentFieldProps?.topSlot;
    $[30] = contextualSlot;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const composerTopSlot = t12;
  const shouldRenderCommentField = hasCommentField && (composerTopSlot !== undefined || isLogin);
  let t13;
  if ($[32] !== composerMode.kind || $[33] !== handleCommentPostSuccess) {
    t13 = result => {
      if (result.type === "edit") {
        clearComposerMode();
        scrollToCommentTarget(result.commentId);
        return;
      }
      if (!result.response) {
        throw new CustomError();
      }
      const point = result.response.pointActivity?.resultYN ? result.response.pointActivity.addPoints : undefined;
      const isRootComment = composerMode.kind !== "reply";
      clearComposerMode();
      if (isRootComment) {
        setPostNonce(_temp3);
      }
      handleCommentPostSuccess(result.response.welloCommentId, point);
    };
    $[32] = composerMode.kind;
    $[33] = handleCommentPostSuccess;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const handleFieldSuccess = t13;
  let t14;
  if ($[35] !== className || $[36] !== clearEditModeByDeletedComment || $[37] !== commentData || $[38] !== commentType || $[39] !== contentId || $[40] !== handleChangeSortType || $[41] !== handleClickEditButton || $[42] !== handleClickReplyButton || $[43] !== initialPageData || $[44] !== postNonce || $[45] !== rootCommentId || $[46] !== sortType) {
    t14 = <CommentList className={className} commentType={commentType} contentId={contentId} initialData={commentData} initialPageData={initialPageData} postNonce={postNonce} rootCommentId={rootCommentId} sortType={sortType} onDeleteSuccess={clearEditModeByDeletedComment} onChangeCommentData={setCommentData} onChangeSortType={handleChangeSortType} onClickEditButton={handleClickEditButton} onClickReplyButton={handleClickReplyButton} />;
    $[35] = className;
    $[36] = clearEditModeByDeletedComment;
    $[37] = commentData;
    $[38] = commentType;
    $[39] = contentId;
    $[40] = handleChangeSortType;
    $[41] = handleClickEditButton;
    $[42] = handleClickReplyButton;
    $[43] = initialPageData;
    $[44] = postNonce;
    $[45] = rootCommentId;
    $[46] = sortType;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== commentFieldProps || $[49] !== commentType || $[50] !== composerMode.commentId || $[51] !== composerMode.contents || $[52] !== composerMode.kind || $[53] !== composerMode.parentCommentId || $[54] !== composerMode.replyTo || $[55] !== composerMode.rootCommentId || $[56] !== composerTopSlot || $[57] !== contentId || $[58] !== focusTrigger || $[59] !== handleFieldSuccess || $[60] !== initOnFocus || $[61] !== rootCommentId || $[62] !== shouldRenderCommentField) {
    t15 = shouldRenderCommentField ? <CommentField commentType={commentType} contentId={contentId} initOnFocus={initOnFocus} parentCommentId={composerMode.kind === "reply" ? composerMode.parentCommentId : undefined} replyTo={composerMode.kind === "reply" ? composerMode.replyTo : undefined} rootCommentId={composerMode.kind === "reply" ? composerMode.rootCommentId : rootCommentId} editingCommentId={composerMode.kind === "edit" ? composerMode.commentId : undefined} editingContents={composerMode.kind === "edit" ? composerMode.contents : undefined} focusTrigger={focusTrigger || undefined} {...commentFieldProps} topSlot={composerTopSlot} onSuccess={handleFieldSuccess} /> : null;
    $[48] = commentFieldProps;
    $[49] = commentType;
    $[50] = composerMode.commentId;
    $[51] = composerMode.contents;
    $[52] = composerMode.kind;
    $[53] = composerMode.parentCommentId;
    $[54] = composerMode.replyTo;
    $[55] = composerMode.rootCommentId;
    $[56] = composerTopSlot;
    $[57] = contentId;
    $[58] = focusTrigger;
    $[59] = handleFieldSuccess;
    $[60] = initOnFocus;
    $[61] = rootCommentId;
    $[62] = shouldRenderCommentField;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== t14 || $[65] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  return t16;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(prev) {
  return prev + 1;
}
function _temp3(prev_0) {
  return prev_0 + 1;
}